import { useState, useCallback } from 'react'
import { api } from '../lib/api'

// Caché global para las signed URLs
const imageCache = new Map<string, { url: string; timestamp: number }>()
const loadingPromises = new Map<string, Promise<string>>()

// Tiempo de expiración del caché: 50 minutos (las signed URLs duran 1 hora)
const CACHE_EXPIRY = 50 * 60 * 1000

export const useImageCache = () => {
  const [cache, setCache] = useState<Map<string, { url: string; timestamp: number }>>(imageCache)

  const getCachedUrl = useCallback((imageKey: string | null): string | null => {
    if (!imageKey) return null

    const cached = imageCache.get(imageKey)
    if (cached) {
      const now = Date.now()
      // Si el caché no ha expirado, devolver la URL
      if (now - cached.timestamp < CACHE_EXPIRY) {
        return cached.url
      } else {
        // Si expiró, eliminar del caché
        imageCache.delete(imageKey)
      }
    }

    return null
  }, [])

  const loadImageUrl = useCallback(async (imageKey: string): Promise<string | null> => {
    if (!imageKey) return null

    // Verificar caché primero
    const cached = getCachedUrl(imageKey)
    if (cached) {
      return cached
    }

    // Si ya hay una petición en curso para esta imagen, esperar a que termine
    // Esto previene peticiones duplicadas incluso con React StrictMode
    const existingPromise = loadingPromises.get(imageKey)
    if (existingPromise) {
      return existingPromise
    }

    // Crear nueva petición
    const promise = (async () => {
      try {
        const response = await api.get(`/upload/signed-url/${encodeURIComponent(imageKey)}`)
        if (response.data?.success && response.data.data?.signedUrl) {
          const url = response.data.data.signedUrl
          imageCache.set(imageKey, { url, timestamp: Date.now() })
          setCache(new Map(imageCache))
          return url
        } else if (response.data?.success && response.data.data?.url) {
          const url = response.data.data.url
          imageCache.set(imageKey, { url, timestamp: Date.now() })
          setCache(new Map(imageCache))
          return url
        }
        return null
      } catch (error) {
        console.error('Error loading image signed URL:', error)
        return null
      } finally {
        loadingPromises.delete(imageKey)
      }
    })()

    loadingPromises.set(imageKey, promise)
    return promise
  }, [getCachedUrl])

  const preloadImages = useCallback(async (imageKeys: (string | null)[]): Promise<void> => {
    const validKeys = imageKeys.filter((key): key is string => key !== null && key !== '')
    const keysToLoad = validKeys.filter(key => !getCachedUrl(key))

    if (keysToLoad.length === 0) return

    // Cargar todas las imágenes en paralelo
    await Promise.all(keysToLoad.map(key => loadImageUrl(key)))
  }, [getCachedUrl, loadImageUrl])

  const clearCache = useCallback(() => {
    imageCache.clear()
    loadingPromises.clear()
    setCache(new Map())
  }, [])

  return {
    getCachedUrl,
    loadImageUrl,
    preloadImages,
    clearCache
  }
}

